// plugins/whyApi.js
import apiBaseState from "~/constants/apiBaseState";
import getFormDataAndTransformMedia from "~/utils/getFormDataAndTransformMedia";
import { appendQueryParams } from "~/utils/appendQueryParams";
import toastr from "toastr";

// Build a friendly error message (preserves server message if present)
const getErrorMessage = (e, message) => {
    const messageDetail = e?.response?.data?.message ? ` - ${e.response.data.message}` : "";
    return `${message}${messageDetail}`;
};

// Build Axios config (now supports AbortController via `signal`)
const getApiConfig = ({ method, url, data, params, handleMedia, signal }) => {
    const config = { method, url };

    if (data) config.data = data;
    if (params) config.params = params;
    if (signal) config.signal = signal; // Axios v1+ supports AbortController

    if (handleMedia) {
        const formData = getFormDataAndTransformMedia({ payload: data });
        config.data = formData;
        config.headers = { "content-type": "multipart/form-data" };
    }

    return config;
};

// Legacy (non-streaming) requests via Axios
const request = async ({
                           $axios,
                           commit,
                           data,
                           method,
                           mutation,
                           url,
                           params,
                           errorMessage,
                           isRefinementSearch = false,
                           handleErrorLocally = false,
                           pauseMutations = false,
                           useProgressIndicator = false,
                           resetDataInitially = true,
                           notify,
                           handleMedia,
                           signal, // <- optional AbortController signal
                       }) => {
    const config = getApiConfig({ method, url, data, params, handleMedia, signal });

    if (useProgressIndicator) {
        config.onUploadProgress = (e) => {
            const rawPct = Math.floor((e.loaded / e.total) * 100);
            const pct = rawPct >= 100 ? 99 : rawPct;
            commit(mutation, { loading: true, percentage: pct });
        };
    }

    const base = apiBaseState();
    base.loading = true;
    if (!resetDataInitially) {
        delete base.data;
    }

    if (!isRefinementSearch) {
        if (!pauseMutations) {
            commit(mutation, base);
        }
    }
    config.handleErrorLocally = handleErrorLocally;

    try {
        const response = await $axios(config);
        commit(mutation, {
            data: response.data,
            loading: false,
                ...(useProgressIndicator ? { percentage: 100 } : {}),
        });
        return response;
    } catch (e) {
        const isAbort =
            e?.name === "AbortError" ||
            e?.name === "CanceledError" ||
            e?.message === "canceled";

        if (isAbort) {
            if (!pauseMutations) {
                commit(mutation, {
                    loading: false,
                    ...(useProgressIndicator ? { percentage: 0 } : {}),
                });
            }
            return Promise.reject(e);
        }

        const errorMessageBuilt = getErrorMessage(e, errorMessage);

        if (!pauseMutations) {
            commit(mutation, {
                loading: false,
                data: null,
                errorMessage: errorMessageBuilt,
                errorStatus: e?.response ? e.response.status : null,
                ...(useProgressIndicator ? { percentage: 0 } : {}),
            });
        }

        if (notify && !handleErrorLocally) {
            notify({
                title: "Error",
                text: errorMessageBuilt,
                width: "50%",
                type: "error",
                duration: 5000,
            });
        }
        return Promise.reject(e);
    }
};

// Streaming requests via fetch (SSE/NDJSON style)
// Streaming requests via fetch (SSE/NDJSON style)
const requestStream = async ({
                                 $axios,
                                 commit,
                                 data,
                                 method,
                                 mutation,
                                 url,
                                 params,
                                 errorMessage,
                                 handleErrorLocally = false,
                                 pauseMutations = false,
                                 notify,
                                 handleMedia,
                                 handleStream = null,
                                 signal,
                             }) => {

    // Build "Axios-style" FormData — convert all values to strings
    const formData = new FormData();
    Object.entries(data || {}).forEach(([key, value]) => {
        // Axios always stringifies values when sending multipart
        const val =
            value === null || value === undefined ? "" : String(value);
        formData.append(key, val);
    });

    // Build URL & config
    const config = getApiConfig({ method, url, data, params, handleMedia });

    const base = apiBaseState();
    base.loading = true;

    if (!pauseMutations) commit(mutation, base);

    try {
        const runtimeConfig = useRuntimeConfig();
        const baseUrl = runtimeConfig.public.apiBase;
        const fullUrl = `${baseUrl}${appendQueryParams(config.url, params)}`;
        const authHeader =
            $axios.defaults.headers.common?.Authorization
                ? { Authorization: $axios.defaults.headers.common.Authorization }
                : {};
        // STREAM using fetch (browser requirement)
        const res = await fetch(fullUrl, {
            method: method.toUpperCase(),
            headers: {
                Accept: "text/event-stream",
                ...authHeader,
            },
            body: formData,
            signal,
        });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        if (!res.body) throw new Error("Readable stream not supported.");

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let accumulated = "";
        let buffer = "";

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            let idx;
            while ((idx = buffer.indexOf("\n")) >= 0) {
                let line = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 1);

                if (!line) continue;

                // strip "data: " prefix
                while (line.startsWith("data: ")) line = line.slice(6).trim();

                if (line === "[DONE]") continue;

                let json = null;
                try {
                    json = JSON.parse(line);
                    if (json?.text) accumulated += json.text;
                } catch (_) {
                    // ignore invalid
                }

                if (!pauseMutations) {
                    commit(mutation, {
                        data: accumulated,
                        loading: true,
                        streaming: "IN_PROGRESS",
                        errorMessage: null,
                        errorStatus: null,
                    });
                }

                if (handleStream) handleStream(json);
            }
        }

        // Finished
        if (!pauseMutations) {
            commit(mutation, {
                data: accumulated,
                loading: false,
                streaming: null,
                errorMessage: null,
                errorStatus: null,
            });
        }

        return accumulated;

    } catch (e) {
        const isAbort =
            e?.name === "AbortError" ||
            e?.name === "CanceledError" ||
            e?.message === "canceled";

        if (isAbort) {
            if (!pauseMutations) {
                commit(mutation, { loading: false, streaming: null });
            }
            return Promise.reject(e);
        }

        const built = getErrorMessage(e, errorMessage);
        commit(mutation, {
            loading: false,
            data: null,
            streaming: null,
            errorMessage: built,
            errorStatus: e?.response ? e.response.status : null,
        });

        if (notify && !handleErrorLocally) {
            notify({
                title: "Error",
                text: built,
                width: "50%",
                type: "error",
                duration: 5000,
            });
        }

        return Promise.reject(e);
    }
};

// Public API factory (keeps your original shape, now signal-aware)
export const requests = (context) => {
    const req = (data, method) => request({ ...data, ...context, method });

    return {
        get: (data) => req(data, "get"),
        put: (data) => req(data, "put"),
        post: (data) => req(data, "post"),
        delete: (data) => req(data, "delete"),
        stream: (data) =>
            requestStream({
                ...data,
                ...context,
                method: data.streamMethod || "post",
            }),
    };
};

// Nuxt plugin entry — attaches to store and (optionally) injects
export default defineNuxtPlugin(nuxtApp => {

    const $axios = nuxtApp.$axios;
    const store = nuxtApp.$vuex;
    const notify = nuxtApp.$notify;

    const whyApi = requests({ $axios, commit: store.commit }); // uncomment if you also want `this.$whyApi`
    store.$whyApi = whyApi;
    store.$notify = notify;

    nuxtApp.provide('whyApi', whyApi);

});
